<script setup lang="ts">
import {
  Home, Building2, BarChart3, ClipboardList, Table2, Landmark,
  TrendingUp, UserCog, Layers, Network, GitBranch,
  Search, ChevronDown,
} from 'lucide-vue-next'
import {
  TooltipProvider, Tooltip, TooltipTrigger, TooltipContent,
} from '@/components/ui/tooltip'
import type { Component } from 'vue'
import { useChromeMenuItems, MenuItemType, type NavItem } from '~/composables/useChromeMenuItems'
import { useMe } from '~/composables/useMe'
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_EXPANDED_WIDTH } from '~/composables/useAppLayout'
import { Button } from '@/components/ui/button'


const t = useTranslator()

const iconMap: Record<string, Component> = {
  search: Search,
  home: Home,
  location_city: Building2,
  bar_chart: BarChart3,
  rubric: ClipboardList,
  table_view: Table2,
  account_balance: Landmark,
  area_chart: TrendingUp,
  manage_accounts: UserCog,
  category: Layers,
  hub: Network,
  graph_1: GitBranch,
}

const getIcon = (iconId?: string): Component | undefined =>
  iconId ? iconMap[iconId] : undefined

const menuItems = useChromeMenuItems()

const searchItem = computed(() =>
  menuItems.homeItems.value.find(i => i.type === MenuItemType.SEARCH)
)
const assetsItem = computed(() =>
  menuItems.homeItems.value.find(i => i.type === MenuItemType.ASSETS)
)
const primaryNavItems = computed(() =>
  menuItems.homeItems.value.filter(i => i.type !== MenuItemType.SEARCH && i.type !== MenuItemType.ASSETS)
)

const sidebarWidth = computed(() => (isSidebarExpanded.value ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH))

const isActive = (item: NavItem) => item.active.value

const expandedItems = ref(new Set<string>())
const isExpanded = (item: NavItem) =>
  expandedItems.value.has(item.type) ||
  (item.children?.some(c => c.active.value) ?? false)

const toggleExpanded = (item: NavItem) => {
  const next = new Set(expandedItems.value)
  if (next.has(item.type)) next.delete(item.type)
  else next.add(item.type)
  expandedItems.value = next
}

const route = useRoute()
watch(() => route.path, () => {
  const allItems = [
    ...primaryNavItems.value,
    ...menuItems.moduleItems.value,
    ...menuItems.settingsItems.value,
  ]
  const next = new Set(expandedItems.value)
  for (const type of next) {
    const item = allItems.find(i => i.type === type)
    if (item && !item.active.value && !item.children?.some(c => c.active.value)) {
      next.delete(type)
    }
  }
  expandedItems.value = next
})

const settingsItem = computed(() => menuItems.settingsItems.value[0])

const { isSidebarExpanded } = useAppLayout()

const handleModuleItemClick = (item: NavItem) => {
  item.onClick?.()
  if (item.children?.length) {
    toggleExpanded(item)
  }
}

const hasWarningBadge = (item: NavItem): boolean =>
  item.warningBadge ||
  (item.children?.some(c => c.warningBadge) ?? false)

const getWarningMessage = (item: NavItem): string | undefined => {
  if (item.warningMessage) return item.warningMessage
  const msgs = item.children?.filter(c => c.warningMessage).map(c => c.warningMessage as string)
  return msgs?.length ? msgs.join(', ') : undefined
}

const { result: meResult } = useMe()
const orgName = computed(() => meResult.value?.me?.organization?.name ?? '')
const orgInitial = computed(() => orgName.value.charAt(0).toUpperCase() || 'A')
const userName = computed(() => meResult.value?.me?.name ?? '')
const userEmail = computed(() => meResult.value?.me?.email ?? '')
const userInitials = computed(() => {
  const parts = userName.value.trim().split(' ').filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return userName.value.substring(0, 2).toUpperCase() || '?'
})
</script>

<template>
  <TooltipProvider :delayDuration="200">
  <div
    class="fixed left-0 top-0 h-screen bg-sidebar border-r border-sidebar-border flex flex-col z-50 overflow-hidden transition-[width] duration-300 ease-in-out"
    :style="{ width: `${sidebarWidth}rem` }"
  >
    <!-- ── Org header ──────────────────────────────────────────── -->
    <div
      class="w-full h-14 flex items-center gap-2.5 px-3.25 hover:bg-sidebar-accent/60 transition-colors cursor-pointer border-b border-sidebar-border shrink-0"
      :class="!isSidebarExpanded ? 'justify-center' : ''"
    >
        <div class="w-7 h-7 bg-sidebar-primary/40 flex items-center justify-center shrink-0 font-bold text-sidebar-foreground text-sm select-none">
          {{ orgInitial }}
        </div>
        <template v-if="isSidebarExpanded">
          <div class="flex-1 min-w-0">
            <p class="text-[0.8125rem] font-semibold text-white leading-tight truncate">{{ orgName }}</p>
          </div>
        </template>
    </div>

    <!-- ── Scrollable nav ──────────────────────────────────────── -->
    <div class="flex-1 overflow-y-auto overflow-x-hidden py-2">

      <!-- NAVIGATION section header — always rendered so height is preserved when collapsed -->
      <div class="px-5 pt-3 pb-1 h-8 flex items-end">
        <span
          class="text-[0.625rem] font-semibold text-sidebar-foreground/50 uppercase tracking-wider select-none whitespace-nowrap transition-opacity duration-200"
          :class="isSidebarExpanded ? 'opacity-100' : 'opacity-0'"
        >{{ t('SIDEBAR_SECTION_NAVIGATION') }}</span>
      </div>

      <!-- Search + primary nav in one container for consistent icon alignment -->
      <div class="space-y-0.5 px-2 mb-1">
        <!-- Search -->
        <Tooltip :disabled="isSidebarExpanded">
          <TooltipTrigger asChild>
            <button
              type="button"
              class="w-full flex items-center px-3 py-2 text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors cursor-pointer"
              :class="!isSidebarExpanded ? 'justify-center' : ''"
              @click="searchItem?.onClick?.()"
            >
              <Search class="w-4.25 h-4.25 shrink-0 text-sidebar-foreground/85" :strokeWidth="1.6" />
              <!-- Morph: max-width + opacity transition instead of v-if -->
              <div
                class="overflow-hidden"
                :style="{
                  maxWidth: isSidebarExpanded ? '200px' : '0px',
                  opacity: isSidebarExpanded ? 1 : 0,
                  marginLeft: isSidebarExpanded ? '0.75rem' : '0px',
                  transition: 'max-width 0.3s ease-in-out, opacity 0.2s ease-in-out, margin-left 0.3s ease-in-out',
                }"
              >
                <span class="text-[0.844rem] whitespace-nowrap leading-none">{{ searchItem?.labelText }}</span>
              </div>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" :sideOffset="8">{{ searchItem?.labelText }}</TooltipContent>
        </Tooltip>

        <!-- Primary nav items -->
        <Tooltip
          v-for="item in primaryNavItems"
          :key="item.type"
          :disabled="isSidebarExpanded"
        >
          <TooltipTrigger asChild>
            <NuxtLink
              :to="item.route"
              activeClass=""
              exactActiveClass=""
              class="no-underline! w-full flex items-center gap-3 px-3 py-2 transition-colors border-l-2"
              :class="[
                !isSidebarExpanded ? 'justify-center' : '',
                isActive(item)
                  ? 'bg-sidebar-primary text-sidebar-primary-foreground! border-chart-4'
                  : 'text-sidebar-foreground! hover:bg-sidebar-accent hover:text-sidebar-accent-foreground! border-transparent',
              ]"
              @click="item.onClick?.()"
            >
              <component
                :is="getIcon(item.iconId)"
                class="w-4.25 h-4.25 shrink-0"
                :class="isActive(item) ? 'text-sidebar-primary-foreground!' : 'text-sidebar-foreground/85!'"
                :strokeWidth="1.6"
              />
              <span v-if="isSidebarExpanded" class="text-[0.844rem] flex-1 text-left whitespace-nowrap leading-none">{{ item.labelText }}</span>
            </NuxtLink>
          </TooltipTrigger>
          <TooltipContent side="right" :sideOffset="8">{{ item.labelText }}</TooltipContent>
        </Tooltip>
      </div>

      <!-- MODULES section header — always rendered so height is preserved when collapsed -->
      <div class="px-5 pt-3 pb-1 h-8 flex items-end">
        <span
          class="text-[0.625rem] font-semibold text-sidebar-foreground/50 uppercase tracking-wider select-none whitespace-nowrap transition-opacity duration-200"
          :class="isSidebarExpanded ? 'opacity-100' : 'opacity-0'"
        >{{ t('SIDEBAR_SECTION_MODULES') }}</span>
      </div>

      <!-- Modules -->
      <div class="space-y-0.5 px-2">
        <!-- Assets -->
        <Tooltip v-if="assetsItem" :disabled="isSidebarExpanded">
          <TooltipTrigger asChild>
            <NuxtLink
              :to="assetsItem.route"
              activeClass=""
              exactActiveClass=""
              class="no-underline! w-full flex items-center gap-3 px-3 py-2 transition-colors border-l-2"
              :class="[
                !isSidebarExpanded ? 'justify-center' : '',
                isActive(assetsItem)
                  ? 'bg-sidebar-primary hover:bg-sidebar-primary/80 text-sidebar-primary-foreground! border-chart-4'
                  : 'text-sidebar-foreground! hover:bg-sidebar-accent hover:text-sidebar-accent-foreground! border-transparent',
              ]"
              @click="assetsItem.onClick?.()"
            >
              <component
                :is="getIcon(assetsItem.iconId)"
                class="w-4.25 h-4.25 shrink-0"
                :class="isActive(assetsItem) ? 'text-sidebar-primary-foreground!' : 'text-sidebar-foreground/85!'"
                :strokeWidth="1.6"
              />
              <span v-if="isSidebarExpanded" class="text-[0.844rem] flex-1 text-left whitespace-nowrap leading-none">{{ assetsItem.labelText }}</span>
            </NuxtLink>
          </TooltipTrigger>
          <TooltipContent side="right" :sideOffset="8">{{ assetsItem.labelText }}</TooltipContent>
        </Tooltip>

        <template v-for="item in menuItems.moduleItems.value" :key="item.type">
          <!-- Expanded with children: split layout so chevron only toggles, link only navigates -->
          <div
            v-if="isSidebarExpanded && item.children?.length"
            class="relative w-full flex items-center transition-colors border-l-2"
            :class="[
              isActive(item)
                ? 'bg-sidebar-primary text-sidebar-primary-foreground! border-chart-4'
                : 'text-sidebar-foreground! hover:bg-sidebar-accent hover:text-sidebar-accent-foreground! border-transparent',
            ]"
          >
            <!-- Navigation area: icon + label -->
            <NuxtLink
              :to="item.route ?? '#'"
              activeClass=""
              exactActiveClass=""
              class="no-underline! flex flex-1 items-center gap-3 pl-3 pr-1 py-2 min-w-0"
              @click="item.onClick?.()"
            >
              <component
                :is="getIcon(item.iconId)"
                class="w-4.25 h-4.25 shrink-0"
                :class="isActive(item) ? 'text-sidebar-primary-foreground!' : 'text-sidebar-foreground/85!'"
                :strokeWidth="1.6"
              />
              <span class="text-[0.844rem] flex-1 text-left whitespace-nowrap leading-none">{{ item.labelText }}</span>
              <span
                v-if="item.badge && item.badge !== '0'"
                class="bg-destructive text-destructive-foreground text-[0.625rem] font-semibold px-1.25 py-0.5 min-w-4.5 text-center leading-none"
              >{{ item.badge }}</span>
            </NuxtLink>
            <!-- Chevron toggle — @click.stop prevents navigation -->
            <button
              type="button"
              class="pr-3 pl-1.5 self-stretch flex items-center group/chev cursor-pointer"
              :aria-label="isExpanded(item) ? 'Skjul undermenu' : 'Vis undermenu'"
              @click.stop="toggleExpanded(item)"
            >
              <span class="w-5.5 h-5.5 flex items-center justify-center transition-all duration-150 group-hover/chev:bg-white/10 group-hover/chev:scale-110">
                <ChevronDown
                  class="w-3 h-3 shrink-0 transition-transform duration-200"
                  :class="[
                    isExpanded(item) ? 'rotate-180' : '',
                    isActive(item) ? 'text-sidebar-primary-foreground/70!' : 'text-sidebar-foreground/70! group-hover/chev:text-sidebar-accent-foreground!',
                  ]"
                  :strokeWidth="2"
                />
              </span>
            </button>
          </div>

          <!-- Collapsed or no children: original single-link layout -->
          <Tooltip v-else :disabled="isSidebarExpanded">
            <TooltipTrigger asChild>
              <NuxtLink
                :to="item.route ?? '#'"
                activeClass=""
                exactActiveClass=""
                class="relative no-underline! w-full flex items-center gap-3 px-3 py-2 border-l-2"
                :class="[
                  !isSidebarExpanded ? 'justify-center' : '',
                  isActive(item)
                    ? 'bg-sidebar-primary hover:bg-sidebar-primary/80 text-sidebar-primary-foreground! border-chart-4'
                    : 'text-sidebar-foreground! hover:bg-sidebar-accent hover:text-sidebar-accent-foreground! border-transparent',
                ]"
                @click="item.onClick?.()"
              >
                <component
                  :is="getIcon(item.iconId)"
                  class="w-4.25 h-4.25 shrink-0"
                :class="isActive(item) ? 'text-sidebar-primary-foreground!' : 'text-sidebar-foreground/85!'"
                  :strokeWidth="1.6"
                />
                <!-- Collapsed-state badge dot -->
                <span
                  v-if="!isSidebarExpanded && item.badge && item.badge !== '0'"
                  class="absolute top-1 right-1 w-1.5 h-1.5 bg-destructive"
                />
                <template v-if="isSidebarExpanded">
                  <span class="text-[0.844rem] flex-1 text-left whitespace-nowrap leading-none">{{ item.labelText }}</span>
                  <span
                    v-if="item.badge && item.badge !== '0'"
                    class="bg-destructive text-destructive-foreground text-[0.625rem] font-semibold px-1.25 py-0.5 min-w-4.5 text-center leading-none"
                  >{{ item.badge }}</span>
                </template>
              </NuxtLink>
            </TooltipTrigger>
            <TooltipContent side="right" :sideOffset="8">
              {{ item.labelText }}
              <span v-if="getWarningMessage(item)" class="block text-[0.6875rem] mt-0.5 opacity-70">{{ getWarningMessage(item) }}</span>
            </TooltipContent>
          </Tooltip>

          <!-- Sub-items -->
          <div
            v-if="isSidebarExpanded && isExpanded(item) && item.children?.length"
            class="pl-9.5 pb-0.5 space-y-0.5"
          >
            <NuxtLink
              v-for="child in item.children"
              :key="`${child.type}-${child.position}`"
              :to="child.route"
              activeClass=""
              exactActiveClass=""
              class="no-underline! block px-2 py-1 text-[0.75rem] transition-colors whitespace-nowrap truncate border-l-2"
              :class="child.active.value
                ? 'bg-sidebar-primary/60 text-sidebar-primary-foreground! border-chart-4'
                : 'text-sidebar-foreground/80! hover:text-sidebar-accent-foreground! hover:bg-sidebar-accent/60 border-transparent'"
              @click="child.onClick?.()"
            >
              {{ child.labelText }}
            </NuxtLink>
          </div>
        </template>
      </div>
    </div>

    <!-- ── Data management (pinned bottom) ────────────────────── -->
    <div v-if="menuItems.dataManagementItems.value.length" class="border-t border-sidebar-border shrink-0 pt-2 pb-1">
      <p v-if="isSidebarExpanded" class="px-5 pt-1 pb-1 text-[0.625rem] font-semibold text-sidebar-foreground/50 uppercase tracking-wider select-none">
        {{ t('SIDEBAR_SECTION_DATA_MANAGEMENT') }}
      </p>
      <div class="space-y-0.5 px-2">
        <template v-for="item in menuItems.dataManagementItems.value" :key="item.type">
          <Tooltip :disabled="isSidebarExpanded">
            <TooltipTrigger asChild>
              <NuxtLink
                :to="item.route"
                activeClass=""
                exactActiveClass=""
                class="relative no-underline! w-full flex items-center gap-3 px-3 py-2 transition-colors border-l-2"
                :class="[
                  !isSidebarExpanded ? 'justify-center' : '',
                  isActive(item)
                    ? 'bg-sidebar-primary text-sidebar-primary-foreground! border-chart-4'
                    : 'text-sidebar-foreground! hover:bg-sidebar-accent hover:text-sidebar-accent-foreground! border-transparent',
                ]"
              >
                <component
                  :is="getIcon(item.iconId)"
                  class="w-4.25 h-4.25 shrink-0"
                  :class="isActive(item) ? 'text-sidebar-primary-foreground!' : 'text-sidebar-foreground/85!'"
                  :strokeWidth="1.6"
                />
                <!-- Collapsed-state warning dot -->
                <span
                  v-if="!isSidebarExpanded && item.badge && item.badge !== '0'"
                  class="absolute top-1 right-1 w-1.5 h-1.5 bg-destructive"
                />
                <template v-if="isSidebarExpanded">
                  <span class="text-[0.844rem] flex-1 text-left whitespace-nowrap leading-none">{{ item.labelText }}</span>
                  <span
                    v-if="item.badge && item.badge !== '0'"
                    class="bg-destructive text-destructive-foreground text-[0.625rem] font-semibold px-1.25 py-0.5 min-w-4.5 text-center leading-none"
                  >{{ item.badge }}</span>
                </template>
              </NuxtLink>
            </TooltipTrigger>
            <TooltipContent side="right" :sideOffset="8">
              {{ item.labelText }}
              <span v-if="getWarningMessage(item)" class="block text-destructive-foreground/70 text-[0.6875rem] mt-0.5">{{ getWarningMessage(item) }}</span>
            </TooltipContent>
          </Tooltip>
        </template>
      </div>
    </div>

    <!-- ── User footer ─────────────────────────────────────────── -->
    <div class="border-t border-sidebar-border shrink-0">
      <Tooltip :disabled="isSidebarExpanded">
        <TooltipTrigger asChild>
          <NuxtLink
            :to="settingsItem?.route"
            class="flex items-center gap-3 px-2.75 py-2.5 hover:bg-sidebar-accent transition-colors !no-underline"
            :class="[
              !isSidebarExpanded ? 'justify-center' : '',
              settingsItem?.active.value ? 'bg-sidebar-accent/60' : '',
            ]"
          >
            <div class="w-8.5 h-8.5 bg-sidebar-primary flex items-center justify-center text-sidebar-primary-foreground text-[0.6875rem] font-semibold shrink-0 tracking-wide select-none">
              {{ userInitials }}
            </div>
            <template v-if="isSidebarExpanded">
              <div class="flex-1 min-w-0">
                <p class="text-[0.8125rem] text-white font-medium whitespace-nowrap overflow-hidden text-ellipsis leading-[1.3]">{{ userName }}</p>
                <p class="text-[0.719rem] text-sidebar-foreground/50 whitespace-nowrap overflow-hidden text-ellipsis leading-[1.3]">{{ userEmail }}</p>
              </div>
              <UserCog class="w-3.75 h-3.75 text-sidebar-foreground/50 shrink-0" :strokeWidth="1.6" />
            </template>
          </NuxtLink>
        </TooltipTrigger>
        <TooltipContent side="right" :sideOffset="8">{{ settingsItem?.labelText }}</TooltipContent>
      </Tooltip>
    </div>
  </div>
  </TooltipProvider>
</template>
